import { createAsyncThunk } from '@reduxjs/toolkit';
import { actionTypes } from './constants';
import {
  addAttendeeNoteApi,
  cancelAndRefundAttendeeApi,
  patchAttendeeApi,
  createAttendeeApi,
  deleteAttendeeApi,
  fetchAttendeeActivitiesApi,
  fetchAttendeeApi,
  fetchAttendeeByEmailApi,
  fetchAttendeeDailyTransactionsApi,
  fetchAttendeeMonthlyTransactionsApi,
  fetchAttendeeNotesApi,
  fetchAttendeesByEventApi,
  fetchAttendeesByEventApiPagination,
  fetchAttendeeTicketsApi,
  fetchCustomerApi,
  fetchCustomersApi,
  fetchEmailActivitiesApi,
  fetchEventWaitlistAttendeesApi,
  fetchOrderApi,
  fetchTicketPdfApi,
  fetchTicketReceiptPdfApi,
  fetchWorkflowsByCustomerIdApi,
  searchAttendeesByEventApi,
  searchAttendeesStatsByEventApi,
  transferAttendeeTicketApi,
  transferOrderApi,
  fetchCartRequestsApi,
  fetchHostPendingRequestsApi,
  updateRequestStatusApi,
  updateAttendeeApi,
  updateAttendeeTicketApi,
  updateCustomerApi,
  fetchContactMessagesApi,
  replyToContactMessageApi,
} from 'src/redesign/features/Attendees/store/api';
import { Attendee } from 'src/types/types';
import { withErrorHandling } from 'src/utils/async-thunk-error-handler';

export const fetchCustomers = createAsyncThunk(
  actionTypes.FETCH_CUSTOMERS,
  async (selectedPage: string | number) => await fetchCustomersApi(selectedPage),
);

export const fetchAttendeesByEvent = createAsyncThunk(
  actionTypes.FETCH_ATTENDEES_BY_EVENT,
  async ({
    eventId,
    offset = 0,
    limit = 10,
  }: {
    eventId: string;
    offset?: number;
    limit?: number;
  }) => await fetchAttendeesByEventApi({ eventId, offset, limit }),
);

export const searchAttendeesByEvent = createAsyncThunk(
  actionTypes.SEARCH_ATTENDEES_BY_EVENT,
  async ({
    eventId,
    offset = 0,
    limit = 10,
    criteria,
  }: {
    eventId?: string;
    offset?: number;
    limit?: number;
    criteria?: Record<string, any>;
    pagination?: boolean;
  }) =>
    await searchAttendeesByEventApi({
      eventId,
      offset,
      limit,
      criteria,
    }),
);

export const searchAttendeesStatsByEvent = createAsyncThunk(
  actionTypes.SEARCH_ATTENDEES_STATS_BY_EVENT,
  async ({
    eventId,
    offset = 0,
    limit = 10,
    criteria,
  }: {
    eventId?: string;
    offset?: number;
    limit?: number;
    criteria?: Record<string, any>;
  }) =>
    await searchAttendeesStatsByEventApi({
      eventId,
      offset,
      limit,
      criteria,
    }),
);

export const fetchEventWaitlistAttendees = createAsyncThunk(
  actionTypes.FETCH_EVENT_WAITLIST_ATTENDEES,
  async (eventId: string) => await fetchEventWaitlistAttendeesApi(eventId),
);

export const fetchAttendeesByEventPagination = createAsyncThunk(
  actionTypes.FETCH_ATTENDEES_BY_EVENT_PAGINATION,
  async ({
    eventId,
    offset = 0,
    limit = 20,
  }: {
    eventId?: string;
    offset?: number;
    limit?: number;
  }) =>
    await fetchAttendeesByEventApiPagination({
      eventId,
      offset,
      limit,
    }),
);

export const fetchWorkflowsByCustomerId = createAsyncThunk(
  actionTypes.FETCH_WORKFLOWS_BY_CUSTOMER_ID,
  async (id: string) => await fetchWorkflowsByCustomerIdApi(id),
);

export const fetchAttendeeMonthlyTransactions = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_MONTHLY_TRANSACTIONS,
  async () => await fetchAttendeeMonthlyTransactionsApi(),
);

export const fetchAttendeeDailyTransactions = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_DAILY_TRANSACTIONS,
  async () => await fetchAttendeeDailyTransactionsApi(),
);

export const fetchCustomer = createAsyncThunk(
  actionTypes.FETCH_CUSTOMER,
  async (id: string) => await fetchCustomerApi(id),
);

export const fetchAttendee = createAsyncThunk(actionTypes.FETCH_ATTENDEE, async (id: string) =>
  fetchAttendeeApi(id),
);

export const fetchAttendeeByEmail = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_BY_EMAIL,
  async () => await fetchAttendeeByEmailApi(),
);

export const deleteAttendee = createAsyncThunk(
  actionTypes.DELETE_ATTENDEE,
  async (id: string) => await deleteAttendeeApi(id),
);

export const createAttendee = createAsyncThunk(
  actionTypes.CREATE_ATTENDEE,
  withErrorHandling(
    (attendeeData: Attendee) => createAttendeeApi(attendeeData),
    'Error creating attendee',
  ),
);

export const addAttendeeNote = createAsyncThunk(
  actionTypes.ADD_ATTENDEE_NOTE,
  async (data: any) => await addAttendeeNoteApi(data),
);

export const fetchAttendeeNotes = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_NOTES,
  async (attendeeId: any) => await fetchAttendeeNotesApi(attendeeId),
);

export const updateAttendee = createAsyncThunk(
  actionTypes.UPDATE_ATTENDEE,
  async (attendee: any) => await updateAttendeeApi(attendee),
);

export const patchAttendee = createAsyncThunk(
  actionTypes.CONVERT_TO_ATTENDEE,
  async (attendee: any) => await patchAttendeeApi(attendee),
);

export const updateAttendeeTicket = createAsyncThunk(
  actionTypes.UPDATE_ATTENDEE_TICKET,
  async (attendee: any) => await updateAttendeeTicketApi(attendee),
);

export const cancelAndRefundAttendee = createAsyncThunk(
  actionTypes.CANCEL_AND_REFUND_ATTENDEE,
  async (data: { cart_id: string; reason: string; requestRefund: boolean }) =>
    await cancelAndRefundAttendeeApi(data),
);

export const updateCustomer = createAsyncThunk(
  actionTypes.UPDATE_CUSTOMER,
  async (customer: any) => await updateCustomerApi(customer),
);

export const fetchOrder = createAsyncThunk(
  actionTypes.FETCH_ORDER,
  async (checkoutId: string) => await fetchOrderApi(checkoutId),
);

export const fetchEmailActivities = createAsyncThunk(
  actionTypes.FETCH_EMAIL_ACTIVITIES,
  async (attendeeId: Attendee['id']) => await fetchEmailActivitiesApi(attendeeId),
);

export const fetchAttendeeActivities = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_ACTIVITIES,
  async (attendeeId: Attendee['id']) => await fetchAttendeeActivitiesApi(attendeeId),
);

export const fetchAttendeeTickets = createAsyncThunk(
  actionTypes.FETCH_ATTENDEE_TICKETS,
  async () => await fetchAttendeeTicketsApi(),
);

export const fetchTicketReceiptPdf = createAsyncThunk(
  actionTypes.FETCH_TICKET_RECEIPT_PDF,
  async (cartId: string) => await fetchTicketReceiptPdfApi(cartId),
);

export const fetchTicketPdf = createAsyncThunk(
  actionTypes.FETCH_TICKET_PDF,
  async (cartId: string) => await fetchTicketPdfApi(cartId),
);

export const transferAttendeeTicket = createAsyncThunk(
  actionTypes.TRANSFER_ATTENDEE_TICKET,
  async (data: { cart_id: string; targetEventId: string; reason: string }) =>
    await transferAttendeeTicketApi(data),
);

export const fetchCartRequests = createAsyncThunk(
  actionTypes.FETCH_CART_REQUESTS,
  async (cartId: string) => await fetchCartRequestsApi(cartId),
);

export const fetchHostPendingRequests = createAsyncThunk(
  actionTypes.FETCH_HOST_PENDING_REQUESTS,
  async () => await fetchHostPendingRequestsApi(),
);

export const updateRequestStatus = createAsyncThunk(
  actionTypes.UPDATE_REQUEST_STATUS,
  async (data: {
    requestId: string;
    status: 'approved' | 'denied';
    admin_notes?: string;
    ticket_assignments?: Record<string, string>;
  }) => await updateRequestStatusApi(data),
);

export const transferOrder = createAsyncThunk(
  actionTypes.TRANSFER_ORDER,
  async (data: {
    orderId: string;
    target_event_id: string;
    reason?: string;
    ticket_assignments?: Record<string, string>;
    send_ticket?: boolean;
  }) => await transferOrderApi(data),
);

export const fetchContactMessages = createAsyncThunk(
  actionTypes.FETCH_CONTACT_MESSAGES,
  async () => await fetchContactMessagesApi(),
);

export const replyToContactMessage = createAsyncThunk(
  actionTypes.REPLY_TO_CONTACT_MESSAGE,
  async (data: { messageId: string; reply: string }) => await replyToContactMessageApi(data),
);
